<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import QRCode from 'qrcode'
import { Activity, Archive, BarChart3, ChevronDown, Copy, Download, Edit3, ExternalLink, FileDown, FileJson, FileOutput, FilePlus2, FileSearch, FileSpreadsheet, FlaskConical, History, KeyRound, LayoutTemplate, Link2, MoreHorizontal, RotateCcw, Search, Send, Share2, ShieldCheck, Sparkles, Trash2, UploadCloud, UsersRound, WandSparkles, X } from '@lucide/vue'
import { ApiError, api, deleteJson, downloadCsv, downloadFile, downloadJson, isLocalCreatorMode, postJson, putJson } from '../api'
import { analyzeLocalSurveyFile } from '../local-document-import'
import type { SurveyDefinition, SurveySummary } from '../types'

type ImportFormat = 'docx' | 'pdf' | 'xlsx' | 'csv' | 'json' | 'txt'
interface ImportAnalysis {
  source: {
    fileName: string
    format: ImportFormat
    mode: 'template' | 'ai'
    sizeBytes: number
    parser: string
    aiEnhanced: boolean
    confidence: 'high' | 'medium' | 'low'
    warnings: string[]
    canCreate: boolean
    blockingIssues: string[]
  }
  draft: {
    title: string
    description: string
    type: 'general'
    category: string
    riskLevel: 'low'
    minAge: number
    accent: string
    estimatedMinutes: number
    definition: SurveyDefinition
    settings: { quota: null; startsAt: null; endsAt: null; responseLimit: 'once'; requireLogin: boolean; allowSaveProgress: boolean }
  }
}
interface PersonalAiStatus { provider: 'deepseek'; configured: boolean; enabled: boolean; validatedAt: string | null }
interface CollectionStatus {
  surveyId: string
  status: string
  acceptingResponses: boolean
  responseCount: number
  todayResponses: number
  lastResponseAt: string | null
  requireLogin: boolean
  responseLimit: 'once' | 'daily' | 'unlimited'
  quota: number | null
  quotaRemaining: number | null
  startsAt: string | null
  endsAt: string | null
  passwordProtected: boolean
}

const surveys = ref<SurveySummary[]>([])
const loading = ref(true)
const message = ref('')
const templates = ref<Array<{ id: string; name: string; description: string; type: string; category: string }>>([])
const deletingId = ref('')
const importing = ref(false)
const dragActive = ref(false)
const importMode = ref<'template' | 'ai'>('template')
const personalAi = ref<PersonalAiStatus>({ provider: 'deepseek', configured: false, enabled: false, validatedAt: null })
const aiSettingsOpen = ref(false)
const deepSeekApiKey = ref('')
const savingAiSettings = ref(false)
const aiSettingsFeedback = ref<{ kind: 'success' | 'error'; text: string } | null>(null)
const importInput = ref<HTMLInputElement | null>(null)
const importAnalysis = ref<ImportAnalysis | null>(null)
const importedSourceFile = ref<File | null>(null)
const creatingImported = ref(false)
const shareTarget = ref<SurveySummary | null>(null)
const shareCopied = ref(false)
const shareQrDataUrl = ref('')
const collectionStatus = ref<CollectionStatus | null>(null)
const collectionStatusLoading = ref(false)
let collectionStatusTimer: number | null = null
const sourceTemplateInput = ref<HTMLInputElement | null>(null)
const sourceTemplateUploading = ref(false)
const sourceTemplate = ref<{ available: boolean; originalName?: string; sizeBytes?: number }>({ available: false })
const offlineInput = ref<HTMLInputElement | null>(null)
const offlineDownloading = ref(false)
const offlineImporting = ref(false)
const offlineDropActive = ref(false)
const offlineImportRows = ref<Array<{ name: string; status: 'imported' | 'duplicate' | 'failed'; message: string }>>([])
const projectQuery = ref('')
const projectStatus = ref('all')
const projectSort = ref<'updated_desc' | 'updated_asc' | 'responses_desc' | 'title_asc'>('updated_desc')
const selectedProjectIds = ref<string[]>([])
const batchArchiving = ref(false)
const router = useRouter()
const standaloneMode = isLocalCreatorMode()
const creatorRoot = standaloneMode ? '/local-create' : '/creator'
const creatorRoute = (suffix = '') => `${creatorRoot}${suffix}`

const totalResponses = computed(() => surveys.value.reduce((sum, item) => sum + item.responseCount, 0))
const published = computed(() => surveys.value.filter((item) => item.status === 'published').length)
const filteredSurveys = computed(() => {
  const query = projectQuery.value.trim().toLocaleLowerCase('zh-CN')
  const rows = surveys.value.filter((survey) => {
    const statusMatches =
      projectStatus.value === 'all'
      || (projectStatus.value === 'approved' && ['published', 'scheduled', 'paused'].includes(survey.status))
      || (projectStatus.value === 'imported' && Boolean(survey.importFormat))
      || (projectStatus.value === 'research' && survey.type === 'research')
      || survey.status === projectStatus.value
    const queryMatches = !query || `${survey.title} ${survey.category}`.toLocaleLowerCase('zh-CN').includes(query)
    return statusMatches && queryMatches
  })
  return rows.sort((a, b) => {
    if (projectSort.value === 'updated_asc') return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    if (projectSort.value === 'responses_desc') return b.responseCount - a.responseCount
    if (projectSort.value === 'title_asc') return a.title.localeCompare(b.title, 'zh-CN')
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })
})
const selectableVisibleIds = computed(() => filteredSurveys.value.filter((survey) => survey.owner !== false && survey.status !== 'archived').map((survey) => survey.id))
const allVisibleSelected = computed(() => selectableVisibleIds.value.length > 0 && selectableVisibleIds.value.every((id) => selectedProjectIds.value.includes(id)))
const shareUrl = computed(() => shareTarget.value && !standaloneMode ? new URL(`/surveys/${encodeURIComponent(shareTarget.value.slug)}`, window.location.origin).toString() : '')
const localShareLink = computed(() => !standaloneMode && ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname))
const offlinePackageAvailable = computed(() => {
  const survey = shareTarget.value
  return Boolean(survey && (standaloneMode || survey.status === 'published') && survey.riskLevel !== 'high' && survey.type !== 'sensitive')
})
const importConfidence = computed(() => {
  const confidence = importAnalysis.value?.source.confidence
  return confidence === 'high'
    ? { label: '高置信度', value: 92, note: '题目结构清晰，仍建议抽查选项与必答设置。' }
    : confidence === 'medium'
      ? { label: '中等置信度', value: 68, note: '部分结构由系统推断，请逐题核对标记项。' }
      : { label: '低置信度', value: 38, note: '源文件结构不稳定，必须完整复核后再生成草稿。' }
})

async function load() {
  const [loadedSurveys, loadedTemplates, aiStatus] = await Promise.all([
    api<SurveySummary[]>('/api/creator/surveys'),
    api<Array<{ id: string; name: string; description: string; type: string; category: string }>>('/api/templates'),
    standaloneMode ? Promise.resolve(null) : api<PersonalAiStatus>('/api/account/ai-providers/deepseek').catch(() => null),
  ])
  surveys.value = loadedSurveys
  selectedProjectIds.value = selectedProjectIds.value.filter((id) => loadedSurveys.some((survey) => survey.id === id))
  templates.value = loadedTemplates
  if (aiStatus) personalAi.value = aiStatus
  loading.value = false
}
onMounted(load)
onBeforeUnmount(() => stopCollectionStatusPolling())

async function publish(survey: SurveySummary) {
  message.value = ''
  try {
    const result = await postJson<{ status: string; version: number }>(`/api/creator/surveys/${survey.id}/submit`, {})
    message.value = `V${result.version} 已申请公开，正在等待站长人工复核。`
    await load()
  } catch (err) { message.value = err instanceof ApiError ? err.message : '提交失败。' }
}

async function duplicateSurvey(survey: SurveySummary) {
  const title = window.prompt('副本名称', `${survey.title}（副本）`)
  if (!title) return
  try {
    await postJson(`/api/creator/surveys/${survey.id}/duplicate`, { title })
    message.value = '已复制为新的草稿项目。'
    await load()
  } catch (err) { message.value = err instanceof ApiError ? err.message : '复制项目失败。' }
}

function exportCsv(survey: SurveySummary) {
  void downloadCsv(`/api/creator/surveys/${survey.id}/export.csv`, `${survey.title}-数据.csv`)
}

function stopCollectionStatusPolling() {
  if (collectionStatusTimer !== null) {
    window.clearInterval(collectionStatusTimer)
    collectionStatusTimer = null
  }
}

async function loadCollectionStatus(surveyId: string, silent = false) {
  if (standaloneMode) return
  if (!silent) collectionStatusLoading.value = true
  try {
    const status = await api<CollectionStatus>(`/api/creator/surveys/${surveyId}/collection-status`)
    if (shareTarget.value?.id === surveyId) collectionStatus.value = status
  } catch (error) {
    if (!silent && shareTarget.value?.id === surveyId) {
      message.value = error instanceof ApiError ? error.message : '暂时无法读取在线回收状态。'
    }
  } finally {
    if (!silent && shareTarget.value?.id === surveyId) collectionStatusLoading.value = false
  }
}

function closeShare() {
  stopCollectionStatusPolling()
  shareTarget.value = null
  collectionStatus.value = null
  collectionStatusLoading.value = false
}

function openShare(survey: SurveySummary) {
  stopCollectionStatusPolling()
  shareTarget.value = survey
  shareCopied.value = false
  shareQrDataUrl.value = ''
  collectionStatus.value = null
  collectionStatusLoading.value = false
  if (!standaloneMode) {
    void loadCollectionStatus(survey.id)
    collectionStatusTimer = window.setInterval(() => void loadCollectionStatus(survey.id, true), 10_000)
  }
  if (!standaloneMode) void nextTick(() => QRCode.toDataURL(shareUrl.value, {
    width: 720,
    margin: 2,
    color: { dark: '#3b2921', light: '#fff9e9' },
    errorCorrectionLevel: 'M',
  }).then((value) => { if (shareTarget.value?.id === survey.id) shareQrDataUrl.value = value }).catch(() => undefined))
  offlineImportRows.value = []
  sourceTemplate.value = { available: false }
  if (survey.importFormat === 'xlsx') {
    void api<{ available: boolean; originalName?: string; sizeBytes?: number }>(`/api/creator/surveys/${survey.id}/source-template`)
      .then((result) => { if (shareTarget.value?.id === survey.id) sourceTemplate.value = result })
      .catch(() => undefined)
  }
}

function downloadShareQr() {
  if (!shareQrDataUrl.value || !shareTarget.value) return
  const link = document.createElement('a')
  link.href = shareQrDataUrl.value
  link.download = `${shareTarget.value.title}-填写二维码.png`
  link.click()
}

async function copyShareLink() {
  if (!shareUrl.value) return
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl.value)
    } else {
      const temporary = document.createElement('textarea')
      temporary.value = shareUrl.value
      temporary.style.position = 'fixed'
      temporary.style.opacity = '0'
      document.body.appendChild(temporary)
      temporary.select()
      document.execCommand('copy')
      temporary.remove()
    }
    shareCopied.value = true
    message.value = '填写链接已复制。完成的答卷会自动回收到当前项目。'
    window.setTimeout(() => { shareCopied.value = false }, 1800)
  } catch {
    message.value = '链接复制失败，请手动复制输入框中的地址。'
  }
}

async function shareSurveyLink() {
  if (!shareTarget.value) return
  if (navigator.share) {
    try {
      await navigator.share({
        title: `邀请填写：${shareTarget.value.title}`,
        text: '请通过这条链接填写问卷；中途退出后，用同一浏览器重新打开即可继续。',
        url: shareUrl.value,
      })
      return
    } catch {
      // The system share sheet may be dismissed. Keep the dialog available.
    }
  }
  await copyShareLink()
}

function openShareAnalytics() {
  const target = shareTarget.value
  closeShare()
  if (target) void router.push(creatorRoute(`/${target.id}/analytics`))
}

function collectionStateLabel(status: CollectionStatus | null) {
  if (!status) return collectionStatusLoading.value ? '正在读取回收状态…' : '回收状态暂不可用'
  if (status.acceptingResponses) return '正在在线回收'
  if (status.status === 'pending') return '等待审核后开始回收'
  if (status.status === 'paused') return '在线回收已暂停'
  if (status.status !== 'published') return '当前尚未开放回收'
  if (status.quota !== null && status.quotaRemaining === 0) return '已达到回收配额'
  if (status.startsAt && new Date(status.startsAt).getTime() > Date.now()) return '尚未到开始时间'
  if (status.endsAt && new Date(status.endsAt).getTime() < Date.now()) return '回收已经结束'
  return '当前暂不接收答卷'
}

function formatCollectionTime(value: string | null | undefined) {
  if (!value) return '暂无提交'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '暂无提交' : date.toLocaleString('zh-CN', { hour12: false })
}

function toggleVisibleProjects() {
  if (allVisibleSelected.value) {
    selectedProjectIds.value = selectedProjectIds.value.filter((id) => !selectableVisibleIds.value.includes(id))
  } else {
    selectedProjectIds.value = [...new Set([...selectedProjectIds.value, ...selectableVisibleIds.value])]
  }
}

async function batchArchive() {
  if (!selectedProjectIds.value.length || batchArchiving.value) return
  if (!window.confirm(`确定归档选中的 ${selectedProjectIds.value.length} 个项目吗？历史版本和答卷不会被删除。`)) return
  batchArchiving.value = true
  try {
    const result = await postJson<{ archived: number }>('/api/creator/surveys/batch-archive', { ids: selectedProjectIds.value })
    message.value = `已归档 ${result.archived} 个项目，可在“已归档”筛选中恢复。`
    selectedProjectIds.value = []
    await load()
  } catch (err) {
    message.value = err instanceof ApiError ? err.message : '批量归档失败。'
  } finally {
    batchArchiving.value = false
  }
}

async function restoreSurvey(survey: SurveySummary) {
  try {
    await postJson(`/api/creator/surveys/${survey.id}/restore`, {})
    message.value = `“${survey.title}”已恢复为可编辑项目。`
    await load()
  } catch (err) {
    message.value = err instanceof ApiError ? err.message : '恢复项目失败。'
  }
}

function offlineDisabledReason(survey: SurveySummary) {
  if (survey.status !== 'published') return '请先发布问卷，固定版本后再生成离线填写包'
  if (survey.riskLevel === 'high' || survey.type === 'sensitive') return '高风险或敏感问卷不能使用离线回收'
  return '生成离线填写包并导入填写者回传的答卷'
}

function downloadTextFile(content: string, fileName: string) {
  const blob = new Blob([content], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

async function downloadOfflinePackage() {
  if (!shareTarget.value || !offlinePackageAvailable.value) return
  offlineDownloading.value = true
  try {
    const result = await postJson<{ fileName: string; html: string }>(`/api/creator/surveys/${shareTarget.value.id}/offline-package`, {})
    downloadTextFile(result.html, result.fileName)
    message.value = '离线填写包已下载。把这个 HTML 文件发给填写者；对方完成后会导出 .lumires.json 答卷文件。'
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '离线填写包生成失败，请稍后再试。'
  } finally {
    offlineDownloading.value = false
  }
}

async function importOfflineFiles(files?: FileList | File[]) {
  if (!shareTarget.value || !files?.length || offlineImporting.value) return
  offlineImporting.value = true
  offlineImportRows.value = []
  const selectedFiles = Array.from(files)
  for (const file of selectedFiles) {
    if (file.size > 1024 * 1024) {
      offlineImportRows.value.push({ name: file.name, status: 'failed', message: '文件超过 1MB，不像正常答卷文件。' })
      continue
    }
    try {
      const payload = JSON.parse(await file.text())
      const result = await postJson<{ status: 'imported' | 'duplicate'; responseId: string }>(`/api/creator/surveys/${shareTarget.value.id}/offline-responses/import`, payload)
      offlineImportRows.value.push({ name: file.name, status: result.status, message: result.status === 'imported' ? '已进入项目数据。' : '已导入过，已跳过。' })
    } catch (error) {
      offlineImportRows.value.push({ name: file.name, status: 'failed', message: error instanceof ApiError ? error.message : '不是可识别的离线答卷文件。' })
    }
  }
  offlineImporting.value = false
  offlineDropActive.value = false
  if (offlineInput.value) offlineInput.value.value = ''
  const imported = offlineImportRows.value.filter((item) => item.status === 'imported').length
  const duplicates = offlineImportRows.value.filter((item) => item.status === 'duplicate').length
  message.value = imported ? `已回收 ${imported} 份离线答卷${duplicates ? `，跳过 ${duplicates} 份重复答卷` : ''}。` : '没有新增离线答卷；请查看下方文件接收结果。'
  await load()
}

function handleOfflineDrop(event: DragEvent) {
  offlineDropActive.value = false
  void importOfflineFiles(event.dataTransfer?.files)
}

const formatLabel: Record<ImportFormat, string> = { docx: 'Word', pdf: 'PDF', xlsx: 'Excel', csv: 'CSV', json: 'JSON', txt: '文本' }

async function analyzeFile(file?: File) {
  if (!file) return
  message.value = ''
  if (standaloneMode) {
    importing.value = true
    importedSourceFile.value = file
    try {
      importAnalysis.value = await analyzeLocalSurveyFile(file, importMode.value) as ImportAnalysis
    } catch (error) {
      importedSourceFile.value = null
      message.value = error instanceof Error ? error.message : '文件分析失败，请检查内容后重试。'
    } finally {
      importing.value = false
      dragActive.value = false
      if (importInput.value) importInput.value.value = ''
    }
    return
  }
  if (file.size > 15 * 1024 * 1024) {
    message.value = '文件超过 15MB，请压缩或拆分后再上传。'
    return
  }
  const accepted = importMode.value === 'template' ? /\.(xlsx|csv)$/i : /\.(docx|pdf|xlsx|csv|json|txt)$/i
  if (!accepted.test(file.name)) {
    message.value = importMode.value === 'template'
      ? '按模板导入请上传下载后的 XLSX 模板或同结构的 CSV 文件。'
      : '请选择 DOCX、PDF、XLSX、CSV、JSON 或 TXT 文件。'
    return
  }
  importing.value = true
  importedSourceFile.value = file
  try {
    const form = new FormData()
    form.append('mode', importMode.value)
    form.append('file', file)
    importAnalysis.value = await api<ImportAnalysis>('/api/creator/imports/analyze', { method: 'POST', body: form })
  } catch (error) {
    importedSourceFile.value = null
    message.value = error instanceof ApiError ? error.message : '文件分析失败，请检查文件内容后重试。'
  } finally {
    importing.value = false
    dragActive.value = false
    if (importInput.value) importInput.value.value = ''
  }
}

async function downloadImportTemplate() {
  if (standaloneMode) {
    const template = '\ufeff题目,题型,选项,必填,维度,提示/说明,最小值,最大值,左标签,右标签\r\n你最近能保持专注吗？,scale,,是,general,,1,5,完全不能,非常能\r\n你更喜欢哪种休息方式？,single,散步|听音乐|睡一会儿,是,general,,,,\r\n'
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '心光量域-本地问卷导入模板.csv'
    link.click()
    URL.revokeObjectURL(url)
    message.value = '本地导入模板已下载；填写后可直接拖回工具。'
    return
  }
  try {
    await downloadFile('/api/creator/imports/template.xlsx', '心光量域-标准问卷导入模板.xlsx')
    message.value = '标准问卷导入模板已下载。填写“题目”工作表后，从左侧入口上传即可。'
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '模板下载失败，请稍后重试。'
  }
}

async function enablePersonalDeepSeek() {
  if (!deepSeekApiKey.value.trim() || savingAiSettings.value) return
  savingAiSettings.value = true
  aiSettingsFeedback.value = null
  try {
    personalAi.value = await putJson<PersonalAiStatus>('/api/account/ai-providers/deepseek', { apiKey: deepSeekApiKey.value.trim() })
    deepSeekApiKey.value = ''
    aiSettingsFeedback.value = { kind: 'success', text: 'DeepSeek API Key 已验证并加密保存，现在可以使用 AI 智能整理。' }
    message.value = '你的 DeepSeek 密钥已验证并加密保存。现在可以使用 AI 智能整理。'
  } catch (error) {
    const text = error instanceof ApiError ? error.message : '无法启用 DeepSeek，请稍后重试。'
    aiSettingsFeedback.value = { kind: 'error', text }
    message.value = text
  } finally {
    savingAiSettings.value = false
  }
}

async function removePersonalDeepSeek() {
  if (savingAiSettings.value || !window.confirm('确定移除已保存的 DeepSeek 密钥吗？之后 AI 智能整理将不再使用它。')) return
  savingAiSettings.value = true
  aiSettingsFeedback.value = null
  try {
    personalAi.value = await deleteJson<PersonalAiStatus>('/api/account/ai-providers/deepseek')
    deepSeekApiKey.value = ''
    aiSettingsFeedback.value = { kind: 'success', text: '已移除已保存的 DeepSeek API Key。' }
    message.value = '已移除你的 DeepSeek 密钥。'
  } catch (error) {
    const text = error instanceof ApiError ? error.message : '移除失败，请稍后重试。'
    aiSettingsFeedback.value = { kind: 'error', text }
    message.value = text
  } finally {
    savingAiSettings.value = false
  }
}

function openAiSettings() {
  if (standaloneMode) {
    message.value = '本地模式不会保存或调用 AI 密钥；DOCX、PDF 与 AI 语义整理请交由管理员在服务器工作台处理。'
    return
  }
  aiSettingsFeedback.value = null
  deepSeekApiKey.value = ''
  aiSettingsOpen.value = true
}

function closeAiSettings() {
  aiSettingsOpen.value = false
  aiSettingsFeedback.value = null
  deepSeekApiKey.value = ''
}

function handleDrop(event: DragEvent) {
  dragActive.value = false
  void analyzeFile(event.dataTransfer?.files?.[0])
}

async function chooseImportMode(mode: 'template' | 'ai') {
  importMode.value = mode
  await nextTick()
  importInput.value?.click()
}

function handleDropForMode(event: DragEvent, mode: 'template' | 'ai') {
  importMode.value = mode
  handleDrop(event)
}

async function createImportedSurvey() {
  if (!importAnalysis.value || importAnalysis.value.source.canCreate === false) {
    message.value = '当前识别结果存在严重结构问题，请重新选择文件或修复后再生成草稿。'
    return
  }
  creatingImported.value = true
  try {
    const result = await postJson<{ id: string }>('/api/creator/surveys', importAnalysis.value.draft)
    const sourceFile = importAnalysis.value.source.format === 'xlsx' ? importedSourceFile.value : null
    importAnalysis.value = null
    if (sourceFile && !standaloneMode) {
      try {
        await uploadSourceTemplate(result.id, sourceFile)
        message.value = '已创建问卷，并安全保留原 Excel 模板；回收答案可按原版式导出。'
      } catch (error) {
        message.value = `问卷已创建，但原 Excel 模板尚未保存：${error instanceof ApiError ? error.message : '请稍后在“发送填写链接”中补存。'}`
      }
    }
    importedSourceFile.value = null
    await router.push(creatorRoute(`/${result.id}/edit`))
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '生成问卷草稿失败。'
  } finally {
    creatingImported.value = false
  }
}

async function uploadSourceTemplate(surveyId: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  return api<{ available: boolean; originalName?: string; sizeBytes?: number }>(`/api/creator/surveys/${surveyId}/source-template`, { method: 'POST', body: form })
}

async function selectSourceTemplate(file?: File) {
  if (!shareTarget.value || !file || sourceTemplateUploading.value) return
  if (!/\.xlsx$/i.test(file.name)) {
    message.value = '请补存与该问卷对应的 XLSX 原始 Excel 文件。'
    return
  }
  sourceTemplateUploading.value = true
  try {
    sourceTemplate.value = await uploadSourceTemplate(shareTarget.value.id, file)
    message.value = '原 Excel 模板已保存。之后按原文件结构导出时，会把每份回收答卷回填到对应工作表。'
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '原 Excel 模板保存失败，请稍后再试。'
  } finally {
    sourceTemplateUploading.value = false
    if (sourceTemplateInput.value) sourceTemplateInput.value.value = ''
  }
}

async function exportSource(survey: SurveySummary) {
  if (!survey.importFormat) return
  try {
    await downloadFile(`/api/creator/surveys/${survey.id}/export-source`, `${survey.title}-填写答案.${survey.importFormat}`)
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '按源格式导出失败。'
  }
}

async function removeSurvey(survey: SurveySummary) {
  const hasHistory = survey.version > 0 || survey.responseCount > 0
  const prompt = hasHistory
    ? `“${survey.title}”已有发布版本或填写记录。删除后会从创作者项目列表和公开端移除，并安全归档历史数据。继续吗？`
    : `确定永久删除草稿“${survey.title}”吗？其中的题目、角色图片、材料和协作信息将一并删除，且无法恢复。`
  if (!window.confirm(prompt)) return
  deletingId.value = survey.id
  message.value = ''
  try {
    const result = await deleteJson<{ action: 'deleted' | 'archived' }>(`/api/creator/surveys/${survey.id}`)
    message.value = result.action === 'archived' ? '项目已从公开端移除并归档，历史数据仍被安全保留。' : '草稿已永久删除。'
    await load()
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '删除项目失败，请稍后重试。'
  } finally {
    deletingId.value = ''
  }
}

const statusLabel: Record<string, string> = { draft: '私密草稿', pending: '站长审核中', scheduled: '审核通过 · 待开放', published: '审核通过', paused: '已结束', restricted: '限制访问', rejected: '已退回', archived: '已归档' }
</script>

<template>
  <div class="dashboard-page section-pad">
    <header class="dashboard-header">
      <div><p class="eyebrow"><Sparkles :size="14"/>{{ standaloneMode ? 'LOCAL CREATOR STUDIO' : 'ADMIN CONTENT STUDIO' }}</p><h1>{{ standaloneMode ? '在自己的浏览器里，' : '在内容工作台里，' }}<br/><em>把好问题变成好体验。</em></h1><p>{{ standaloneMode ? '问卷与答卷只保存在当前浏览器；需要公开时，请导出项目交给管理员审核。' : '导入、转换和编辑默认不公开；完成复核后再由管理员发布。' }}</p></div>
      <RouterLink class="button button-dark" :to="creatorRoute('/new')"><FilePlus2/>新建私密问卷</RouterLink>
    </header>

    <section class="metric-row">
      <article><span><FilePlus2/></span><div><small>我的问卷</small><strong>{{ surveys.length }}</strong></div><i>份</i></article>
      <article><span><UsersRound/></span><div><small>累计回应</small><strong>{{ totalResponses }}</strong></div><i>次</i></article>
      <article><span><Activity/></span><div><small>{{ standaloneMode ? '可离线收集' : '审核后运行' }}</small><strong>{{ published }}</strong></div><i>份</i></article>
      <article><span><FlaskConical/></span><div><small>实验能力</small><strong>A/B</strong></div><i>已启用</i></article>
    </section>

    <p v-if="message" class="dashboard-message">{{ message }}</p>

    <details class="creator-tools-panel">
      <summary>
        <span><WandSparkles/><span><b>创建与导入工具</b><small>需要时再展开模板、文件转换和 AI 整理</small></span></span>
        <span class="creator-tools-summary-actions">空白创建 · 模板 · 文件 · AI <ChevronDown/></span>
      </summary>
      <div class="creator-tools-body">
        <section class="document-import-panel">
          <div class="document-import-copy">
            <p class="eyebrow"><WandSparkles :size="14"/>DOCUMENT TO SURVEY</p>
            <h2>把文件整理成一份完整问卷</h2>
            <p>{{ standaloneMode ? '在浏览器内读取结构化文件，不会上传原文件；复杂 Word、PDF 与 AI 解析需交由管理员处理。' : '选择标准模板导入，或让 AI 辅助识别题目、选项和题型。两种方式只会生成私密草稿，AI 不会替你公开发布。' }}</p>
            <div><span><FileSpreadsheet/>{{ standaloneMode ? '本地：XLSX · CSV · JSON · TXT' : '模板：XLSX · CSV' }}</span><span><WandSparkles/>{{ standaloneMode ? 'AI：需交管理员处理' : 'AI：DOCX · PDF · XLSX' }}</span></div>
          </div>
          <div class="import-route-grid">
            <input ref="importInput" type="file" :accept="standaloneMode ? '.xlsx,.csv,.json,.txt' : importMode === 'template' ? '.xlsx,.csv' : '.docx,.pdf,.xlsx,.csv,.json,.txt'" hidden @change="analyzeFile(($event.target as HTMLInputElement).files?.[0])"/>
            <article :class="['import-route-card', { active: importMode === 'template', loading: importing && importMode === 'template' }]">
              <button type="button" class="import-route-select" :aria-busy="importing && importMode === 'template'" @click="chooseImportMode('template')" @dragenter.prevent="importMode = 'template'; dragActive = true" @dragover.prevent="importMode = 'template'; dragActive = true" @dragleave.prevent="dragActive = false" @drop.prevent="handleDropForMode($event, 'template')">
                <FileSpreadsheet/>
                <strong>{{ importing && importMode === 'template' ? '正在按模板读取…' : '按模板导入' }}</strong>
                <small>字段固定，题目与选项识别更稳定</small>
              </button>
              <button type="button" class="import-template-download" @click="downloadImportTemplate"><Download :size="15"/>下载标准模板</button>
            </article>
            <article :class="['import-route-card', 'ai-route', { active: importMode === 'ai', loading: importing && importMode === 'ai' }]">
              <button type="button" class="import-route-select" :aria-busy="importing && importMode === 'ai'" @click="chooseImportMode('ai')" @dragenter.prevent="importMode = 'ai'; dragActive = true" @dragover.prevent="importMode = 'ai'; dragActive = true" @dragleave.prevent="dragActive = false" @drop.prevent="handleDropForMode($event, 'ai')">
                <WandSparkles/>
                <strong>{{ importing && importMode === 'ai' ? '正在由 AI 整理…' : 'AI 智能整理' }}</strong>
                <small>{{ standaloneMode ? '本地模式仅作结构解析，不调用外部 AI' : '适合格式杂乱的 Word、PDF 与已有量表' }}</small>
              </button>
              <button type="button" class="import-ai-settings" @click="openAiSettings"><KeyRound :size="15"/>{{ standaloneMode ? '本地模式不保存 AI 密钥' : personalAi.enabled ? '个人 DeepSeek 已启用 · 管理密钥' : '设置自己的 DeepSeek 密钥' }}</button>
            </article>
          </div>
        </section>

        <section class="template-strip"><div class="panel-heading"><div><h2>从专业模板开始</h2><p>复制后可完全修改，不会影响模板原件。</p></div><LayoutTemplate/></div><div><RouterLink v-for="item in templates" :key="item.id" :to="`${creatorRoute('/new')}?template=${item.id}`"><LayoutTemplate/><span><strong>{{ item.name }}</strong><small>{{ item.description }}</small></span><i>{{ item.category }}</i></RouterLink></div></section>
      </div>
    </details>

    <section class="dashboard-panel">
      <div class="panel-heading project-panel-heading">
        <div><h2>{{ standaloneMode ? '本地问卷项目' : '私密问卷项目' }}</h2><p>{{ standaloneMode ? '全部内容只保存在当前浏览器；清除浏览器数据前请导出项目 JSON 备份。' : '草稿默认仅自己可见；修改已审核版本会生成新版本，历史回答不会被覆盖。' }}</p></div>
        <div class="project-toolbar">
          <label><Search/><input v-model="projectQuery" type="search" placeholder="搜索问卷或分类" aria-label="搜索问卷项目"/></label>
          <select v-model="projectStatus" aria-label="按状态筛选问卷">
            <option value="all">全部状态</option>
            <option value="draft">私密草稿</option>
            <option value="pending">站长审核中</option>
            <option value="approved">已通过</option>
            <option value="archived">已归档</option>
            <option value="imported">文件导入</option>
            <option value="research">科研问卷</option>
            <option value="rejected">已退回</option>
          </select>
          <select v-model="projectSort" aria-label="项目排序方式">
            <option value="updated_desc">最近更新</option>
            <option value="updated_asc">最早更新</option>
            <option value="responses_desc">回应最多</option>
            <option value="title_asc">标题排序</option>
          </select>
          <span>{{ filteredSurveys.length }} / {{ surveys.length }}</span>
        </div>
      </div>
      <div v-if="filteredSurveys.length" class="project-batch-bar">
        <label><input type="checkbox" :checked="allVisibleSelected" @change="toggleVisibleProjects"/>选择当前筛选中的可归档项目</label>
        <span v-if="selectedProjectIds.length">已选 {{ selectedProjectIds.length }} 项</span>
        <button v-if="selectedProjectIds.length" type="button" class="batch-archive-button" :disabled="batchArchiving" @click="batchArchive"><Archive/>{{ batchArchiving ? '正在归档…' : '批量归档' }}</button>
      </div>
      <div v-if="loading" class="table-loading">正在读取项目…</div>
      <div v-else-if="filteredSurveys.length" class="project-list">
        <article v-for="survey in filteredSurveys" :key="survey.id" :class="['project-row', { selected: selectedProjectIds.includes(survey.id), archived: survey.status === 'archived' }]">
          <label class="project-select" :title="survey.owner === false ? '协作项目只能由所有者归档' : survey.status === 'archived' ? '已归档项目请先恢复' : '选择项目'">
            <input v-model="selectedProjectIds" type="checkbox" :value="survey.id" :disabled="survey.owner === false || survey.status === 'archived'"/>
          </label>
          <div class="project-icon" :style="{ background: `${survey.accent}22`, color: survey.accent }">{{ survey.title.slice(0, 1) }}</div>
          <div class="project-main"><div><h3>{{ survey.title }}</h3><span :class="['status-pill', survey.status]">{{ statusLabel[survey.status] || survey.status }}</span><span v-if="survey.listingMode === 'unlisted'" class="status-pill imported">专属链接 · 不入馆</span><span v-if="survey.importFormat" class="status-pill imported">源自 {{ formatLabel[survey.importFormat] }}</span><span v-if="survey.reviewStatus === 'pending'" class="status-pill pending">站长审核中</span></div><p>{{ survey.category }} · V{{ survey.version }} · 更新于 {{ new Date(survey.updatedAt).toLocaleDateString('zh-CN') }}</p></div>
          <div class="project-number"><strong>{{ survey.responseCount }}</strong><small>回应</small></div>
          <div class="project-actions">
            <RouterLink :to="creatorRoute(`/${survey.id}/edit`)"><Edit3/>编辑</RouterLink>
            <RouterLink :to="creatorRoute(`/${survey.id}/analytics`)"><BarChart3/>数据</RouterLink>
            <button type="button" :disabled="!standaloneMode && survey.status !== 'published'" :title="standaloneMode ? '导出离线填写包并回收对方发回的答卷文件' : survey.status === 'published' ? '发送公开填写链接并查看实时回收状态' : '请先发布问卷，再在线发放'" @click="openShare(survey)"><Share2/>{{ standaloneMode ? '离线收集' : '在线发放' }}</button>
            <details class="project-more">
              <summary :aria-label="`打开 ${survey.title} 的更多操作`"><MoreHorizontal/>更多</summary>
              <div>
                <button type="button" @click="duplicateSurvey(survey)"><Copy/>复制为新项目</button>
                <button type="button" @click="downloadJson(`/api/creator/surveys/${survey.id}/export.json`, `${survey.title}-项目.json`)"><FileJson/>导出项目 JSON</button>
                <button type="button" @click="exportCsv(survey)"><Download/>导出原始数据 CSV</button>
                <button v-if="survey.importFormat && !standaloneMode" type="button" @click="exportSource(survey)"><FileOutput/>按源 {{ formatLabel[survey.importFormat] }} 格式导出</button>
                <RouterLink :to="`${creatorRoute(`/${survey.id}/analytics`)}#versions`"><History/>版本记录</RouterLink>
                <button v-if="!standaloneMode" type="button" @click="publish(survey)"><Send/>提交发布复核</button>
                <button v-if="survey.status === 'archived' && survey.owner !== false" type="button" @click="restoreSurvey(survey)"><RotateCcw/>恢复项目</button>
                <button v-if="survey.owner !== false" type="button" class="danger-action" :disabled="deletingId === survey.id" @click="removeSurvey(survey)"><Trash2/>{{ survey.version > 0 || survey.responseCount > 0 ? '删除并归档' : '永久删除草稿' }}</button>
              </div>
            </details>
          </div>
        </article>
      </div>
      <div v-else-if="surveys.length" class="empty-state"><Search/><h2>没有符合条件的项目</h2><p>可以清空搜索词或切换到“全部状态”。</p><button class="button" @click="projectQuery = ''; projectStatus = 'all'">清除筛选</button></div>
      <div v-else class="empty-state"><FilePlus2/><h2>从第一份私密问卷开始</h2><p>你可以创建一般问卷、心理探索或研究实验，完成后再申请公开。</p><RouterLink class="button" :to="creatorRoute('/new')">立即创建</RouterLink></div>
    </section>

    <Transition name="modal">
      <div v-if="aiSettingsOpen" class="modal-backdrop" @click.self="closeAiSettings">
        <section class="ai-settings-modal" aria-labelledby="deepseek-settings-title">
          <button class="modal-close" title="关闭 DeepSeek 设置" aria-label="关闭 DeepSeek 设置" @click="closeAiSettings"><X/></button>
          <p class="eyebrow"><KeyRound :size="14"/>PERSONAL AI</p>
          <h2 id="deepseek-settings-title">启用你的 DeepSeek 智能整理</h2>
          <p>输入你自己的 DeepSeek API Key。网站会先验证，再以加密形式保存在你的账号中；之后仅在你选择“AI 智能整理”时使用。</p>
          <label class="field-label"><span>DeepSeek API Key</span><input v-model="deepSeekApiKey" type="password" autocomplete="off" placeholder="输入你的 DeepSeek API Key" @keyup.enter="enablePersonalDeepSeek"/></label>
          <p class="ai-settings-notice"><ShieldCheck/>密钥不会显示在页面、导出文件或项目数据中。请勿使用他人的密钥，也不要在问卷内容中填写敏感个人信息。</p>
          <p v-if="aiSettingsFeedback" :class="['ai-settings-feedback', aiSettingsFeedback.kind]" role="status">
            <strong>{{ aiSettingsFeedback.kind === 'success' ? '操作成功' : '验证失败' }}</strong>
            <span>{{ aiSettingsFeedback.text }}</span>
          </p>
          <div class="ai-settings-actions">
            <button v-if="personalAi.configured" type="button" class="button button-ghost" :disabled="savingAiSettings" @click="removePersonalDeepSeek"><Trash2/>移除密钥</button>
            <button v-if="aiSettingsFeedback?.kind === 'success'" type="button" class="button button-dark" @click="closeAiSettings">完成</button>
            <button v-else type="button" class="button button-dark" :disabled="savingAiSettings || !deepSeekApiKey.trim()" @click="enablePersonalDeepSeek"><WandSparkles/>{{ savingAiSettings ? '正在验证…' : personalAi.enabled ? '更新并验证' : '验证并启用' }}</button>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal">
      <div v-if="importAnalysis" class="modal-backdrop" @click.self="importAnalysis = null">
        <section class="import-preview-modal">
          <button class="modal-close" title="关闭导入预览" aria-label="关闭导入预览" @click="importAnalysis = null"><X/></button>
          <p class="eyebrow"><FileSearch/>IMPORT PREVIEW</p>
          <div class="import-preview-heading">
            <div><h2>文件已经变成问卷草稿</h2><p>{{ importAnalysis.source.fileName }} · {{ formatLabel[importAnalysis.source.format] }} · {{ (importAnalysis.source.sizeBytes / 1024).toFixed(1) }}KB</p></div>
            <span :class="{ ai: importAnalysis.source.aiEnhanced }"><WandSparkles/>{{ importAnalysis.source.aiEnhanced ? 'AI 语义整理' : importAnalysis.source.parser.includes('ooxml-tables') ? 'Word 表格解析' : '本地安全解析' }}</span>
          </div>
          <label class="field-label"><span>问卷标题</span><input v-model="importAnalysis.draft.title" maxlength="100"/></label>
          <div class="import-summary">
            <span><strong>{{ importAnalysis.draft.definition.questions.length }}</strong>道题</span>
            <span><strong>{{ importAnalysis.draft.estimatedMinutes }}</strong>分钟</span>
            <span><strong>{{ formatLabel[importAnalysis.source.format] }}</strong>答案导出</span>
          </div>
          <div class="import-confidence">
            <div><strong>{{ importConfidence.label }}</strong><span>{{ importConfidence.value }}%</span></div>
            <i><b :style="{ width: `${importConfidence.value}%` }"/></i>
            <p>{{ importConfidence.note }} · 已建立 {{ importAnalysis.draft.definition.sourceImport?.mappings.length || 0 }} 条原文件映射。</p>
          </div>
          <div class="import-question-preview">
            <article v-for="(question, index) in importAnalysis.draft.definition.questions" :key="question.id">
              <i>{{ String(index + 1).padStart(2, '0') }}</i>
              <div><strong>{{ question.prompt }}</strong><small>{{ question.type }}<template v-if="question.options?.length"> · {{ question.options.length }} 个选项</template></small></div>
            </article>
          </div>
          <div v-if="importAnalysis.source.warnings.length" class="import-warnings"><strong>需要你复核</strong><p v-for="warning in importAnalysis.source.warnings" :key="warning">{{ warning }}</p></div>
          <div v-if="importAnalysis.source.blockingIssues?.length" class="import-blocking" role="alert">
            <strong>暂时不能生成草稿</strong>
            <p v-for="issue in importAnalysis.source.blockingIssues" :key="issue">{{ issue }}</p>
          </div>
          <p class="import-privacy"><ShieldCheck/>{{ standaloneMode ? '当前文件只在浏览器内解析，不会上传到心光量域服务器；生成后仍需逐题复核。' : '按模板导入只在本机服务器解析；选择 AI 智能整理且启用 DeepSeek 或外部网关后，系统只发送提取出的文字和可信结构，不发送原始文件。' }}</p>
          <div class="import-preview-actions"><button class="button button-ghost" @click="importAnalysis = null">重新选择</button><button class="button button-dark" :disabled="creatingImported || importAnalysis.source.canCreate === false" @click="createImportedSurvey"><Sparkles/>{{ creatingImported ? '正在生成…' : importAnalysis.source.canCreate === false ? '请先修复识别问题' : '生成可编辑草稿' }}</button></div>
        </section>
      </div>
    </Transition>

    <Transition name="modal">
      <div v-if="shareTarget" class="modal-backdrop" @click.self="closeShare">
        <section class="share-survey-modal" aria-labelledby="share-survey-title">
          <button class="modal-close" title="关闭填写链接" aria-label="关闭填写链接" @click="closeShare"><X/></button>
          <p class="eyebrow"><Link2 :size="14"/>COLLECT RESPONSES</p>
          <h2 id="share-survey-title">{{ standaloneMode ? '导出离线填写包并回收答卷' : '在线发放与自动回收' }}</h2>
          <p>{{ standaloneMode ? '把 HTML 文件发给填写者；对方填写完成后，把导出的答卷文件发回给你，再拖入下方回收。' : `发送链接后，填写者完成提交，答案会直接回收到《${shareTarget.title}》的数据中。` }}</p>

          <label v-if="!standaloneMode" class="field-label share-link-label">
            <span>公开填写链接{{ shareTarget.requireLogin ? '（当前要求登录）' : '（打开即可作答，无需账号）' }}</span>
            <div class="share-link-field">
              <input :value="shareUrl" readonly aria-label="可发送给填写者的问卷链接" @focus="($event.target as HTMLInputElement).select()"/>
              <button type="button" @click="copyShareLink"><Copy :size="16"/>{{ shareCopied ? '已复制' : '复制链接' }}</button>
            </div>
          </label>
          <section v-if="!standaloneMode" class="collection-live-card" :class="{ active: collectionStatus?.acceptingResponses }" aria-live="polite">
            <div class="collection-live-heading">
              <div><i aria-hidden="true"></i><strong>{{ collectionStateLabel(collectionStatus) }}</strong></div>
              <button type="button" :disabled="collectionStatusLoading" @click="shareTarget && loadCollectionStatus(shareTarget.id)">{{ collectionStatusLoading ? '刷新中…' : '刷新状态' }}</button>
            </div>
            <div class="collection-live-metrics">
              <article><strong>{{ collectionStatus?.responseCount ?? shareTarget.responseCount }}</strong><span>累计答卷</span></article>
              <article><strong>{{ collectionStatus?.todayResponses ?? 0 }}</strong><span>今日新增</span></article>
              <article><strong>{{ formatCollectionTime(collectionStatus?.lastResponseAt) }}</strong><span>最近提交</span></article>
            </div>
            <p v-if="collectionStatus?.requireLogin" class="collection-warning">这份问卷当前要求填写者登录。若希望链接打开即可作答，请到编辑器“体验与发布”中关闭该选项并重新提交审核。</p>
            <p v-else>填写者无需注册或登录；点击提交后，答卷会自动进入当前问卷的数据中心，只有问卷所有者、获授权协作者及站点监管人员可以查看。</p>
          </section>
          <section v-if="shareQrDataUrl" class="share-qr-card">
            <img :src="shareQrDataUrl" :alt="`${shareTarget.title}填写二维码`"/>
            <div><h3>问卷二维码</h3><p>下载后可放进海报、班级群或纸质说明。正式部署到域名后，请重新生成一次，避免二维码仍指向本机地址。</p><button type="button" class="button button-ghost" @click="downloadShareQr"><Download/>下载 PNG</button></div>
          </section>

          <div v-if="!standaloneMode" class="share-collection-steps">
            <article><strong>01 · 链接或二维码</strong><p>把公开链接或二维码发给填写者，对方打开后即可阅读说明并开始作答。</p></article>
            <article><strong>02 · 本机自动续填</strong><p>未提交答案会保存在填写者当前浏览器；意外刷新或退出后，用同一设备重新打开即可继续。</p></article>
            <article><strong>03 · 提交即入库</strong><p>点击提交后答卷立即进入你的项目数据，无需再下载、发送或手工导入答卷文件。</p></article>
          </div>

          <p v-if="localShareLink" class="share-local-notice"><ShieldCheck :size="16"/>当前是本机调试地址，只能在这台电脑打开。部署到你的正式域名后，这个按钮会自动生成可供他人访问的链接。</p>
          <p v-else-if="!standaloneMode" class="share-privacy-note">填写者的中途答案仅用于续填；提交后的答卷可在“查看数据”中汇总，或导出 CSV 查看每份原始回答。</p>

          <details class="share-offline-section" :open="standaloneMode || undefined">
            <summary class="share-offline-heading">
              <div><p class="eyebrow"><FileDown :size="14"/>OFFLINE FALLBACK</p><h3 id="offline-package-title">断网或特殊情况下使用离线包</h3></div>
              <span>{{ standaloneMode ? '主要方式' : '备用方式' }} <ChevronDown/></span>
            </summary>
            <div class="share-offline-body">
              <p>在线发放已经可以自动回收，一般无需使用这里。仅在无网络、临时现场或网站无法访问时，才下载 HTML 文件并手工回收 <code>.lumires.json</code> 答卷。</p>
            <div class="share-collection-steps offline-collection-steps">
              <article><strong>01 · 发 HTML</strong><p>通过微信、QQ、邮件或 U 盘发送；填写者双击即可打开。</p></article>
              <article><strong>02 · 自动续填</strong><p>填写进度保存在对方浏览器，退出后仍可在同一设备继续。</p></article>
              <article><strong>03 · 收 JSON</strong><p>填写完成后让对方把下载的答卷文件发回给你。</p></article>
            </div>
            <p v-if="!offlinePackageAvailable" class="share-local-notice"><ShieldCheck :size="16"/>{{ offlineDisabledReason(shareTarget) }}</p>
            <p v-else class="share-privacy-note"><ShieldCheck :size="16"/>离线答卷不会自动上传，也不能验证填写者身份。请只用于低风险且已获得适当同意的问卷。</p>
            <div class="offline-package-actions">
              <button class="button button-dark" :disabled="offlineDownloading || !offlinePackageAvailable" @click="downloadOfflinePackage"><FileDown/>{{ offlineDownloading ? '正在生成…' : '下载离线填写包' }}</button>
              <button v-if="shareTarget.importFormat && !standaloneMode" class="button button-ghost" @click="exportSource(shareTarget)"><FileOutput/>按原文件结构导出</button>
            </div>

            <section v-if="shareTarget.importFormat === 'xlsx' && !standaloneMode" class="source-template-panel">
              <div>
                <h3>按原 Excel 版式导出</h3>
                <p v-if="sourceTemplate.available">已保存《{{ sourceTemplate.originalName }}》。导出时会为每份答卷复制原工作表和样式，再回填对应填写栏位。</p>
                <p v-else>早期导入只记录了题目结构，没有保留原 Excel 文件。补存一次原文件后，后续导出即可沿用原工作表、表头和单元格样式。</p>
              </div>
              <label class="button button-ghost source-template-button" :class="{ loading: sourceTemplateUploading }">
                <input ref="sourceTemplateInput" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden @change="selectSourceTemplate(($event.target as HTMLInputElement).files?.[0])"/>
                <UploadCloud/>{{ sourceTemplateUploading ? '正在保存…' : sourceTemplate.available ? '替换原 Excel 模板' : '补存原 Excel 模板' }}
              </label>
            </section>

            <section class="offline-import-panel">
              <div><h3>回收填写者发回的答卷</h3><p>可一次选择或拖入多份 <code>.lumires.json</code> 文件；已导入的答卷会自动跳过。导入后可直接按原文件结构导出结果。</p></div>
              <div
                :class="['offline-import-dropzone', { active: offlineDropActive, loading: offlineImporting }]"
                role="button"
                tabindex="0"
                :aria-busy="offlineImporting"
                @click="offlineInput?.click()"
                @keydown.enter.prevent="offlineInput?.click()"
                @keydown.space.prevent="offlineInput?.click()"
                @dragenter.prevent="offlineDropActive = true"
                @dragover.prevent="offlineDropActive = true"
                @dragleave.prevent="offlineDropActive = false"
                @drop.prevent="handleOfflineDrop"
              >
                <input ref="offlineInput" type="file" accept=".json,.lumires.json" multiple hidden @change="importOfflineFiles(($event.target as HTMLInputElement).files || undefined)"/>
                <UploadCloud/><strong>{{ offlineImporting ? '正在接收并回收…' : '拖入答卷文件，或点击选择' }}</strong><small>仅接收心光量域离线填写包导出的 JSON 答卷</small>
              </div>
              <ul v-if="offlineImportRows.length" class="offline-import-results">
                <li v-for="row in offlineImportRows" :key="row.name" :class="row.status"><strong>{{ row.name }}</strong><span>{{ row.message }}</span></li>
              </ul>
            </section>
            </div>
          </details>

          <div class="share-survey-actions">
            <a v-if="!standaloneMode" class="button button-ghost" :href="shareUrl" target="_blank" rel="noopener"><ExternalLink/>预览填写页</a>
            <button class="button button-ghost" @click="openShareAnalytics"><BarChart3/>查看回收数据</button>
            <button v-if="!standaloneMode" class="button button-dark" @click="shareSurveyLink"><Share2/>发送填写链接</button>
          </div>
        </section>
      </div>
    </Transition>

  </div>
</template>
